import type { ScriptModule } from "virtual:script";

interface StepBase {
  id: string;
  type: string;
  confetti?: boolean;
}

export interface BasicStep extends StepBase {
  type: "basic";
  header?: { html: string; variant: "default" | "huge" };
  html: string;
  variant?: "default" | "primary";
  buttons: Array<{
    text: string;
    target?: string;
    canPickOnce?: boolean;
    action?: string;
  }>;
}

export interface CountdownStep extends StepBase {
  type: "countdown";
  html: string;
  countdownLength: number;
  target?: string;
  variant?: "default" | "invisible";
}

export interface PrivacyPolicyStep extends StepBase {
  type: "privacy-policy";
  html: string;
  buttons: Array<{
    text: string;
    action?: string;
  }>;
}

export type Step = BasicStep | CountdownStep | PrivacyPolicyStep;

export function getSteps(script: ScriptModule): Step[] {
  return [
    // INFO
    // ----
    {
      id: "info:unmute",
      type: "basic",
      html: script.html("unmute_volume"),
      buttons: [{ text: script.text("unmute_button") }],
    },
    {
      id: "info:tap",
      type: "basic",
      html: script.html("tap_anywhere"),
      buttons: [{ text: script.text("tap_button") }],
      confetti: true,
    },
    {
      id: "info:refresh",
      type: "basic",
      html: script.html("refresh"),
      buttons: [{ text: script.text("refresh_button") }],
    },
    {
      id: "info:tech-support",
      type: "basic",
      html: script.html("tech_table"),
      buttons: [{ text: script.text("tech_button") }],
    },
    // {
    //   id: "info:privacy-policy",
    //   type: "privacy-policy",
    //   html: script.html("privacy_policy"),
    //   buttons: [
    //     { text: script.text("privacy_policy_button1"), action: "agreeToSelfieStorage" },
    //     { text: script.text("privacy_policy_button2"), action: "disagreeToSelfieStorage" },
    //   ],
    // },
    {
      id: "info:notifications",
      type: "basic",
      html: script.html("reminder_notifications"),
      buttons: [{ text: script.text("reminder_button") }],
    },

    // WARMUP
    // ------

    {
      id: "warmup:intro",
      type: "basic",
      html: script.html("waiting_exercises"),
      buttons: [{ text: script.text("waiting_button") }],
    },
    // {
    //   id: "warmup:look-around",
    //   type: "basic",
    //   html: script.html("look_around"),
    //   buttons: [{ text: script.text("look_button1") }, { text: script.text("look_button2") }],
    // },
    // {
    //   id: "warmup:trust",
    //   type: "basic",
    //   html: script.html("trouble"),
    //   buttons: [
    //     { text: script.text("trouble_button1"), target: "warmup:trusting" },
    //     { text: script.text("trouble_button2"), target: "warmup:suspicious" },
    //   ],
    // },
    // {
    //   id: "warmup:trusting",
    //   type: "basic",
    //   html: script.html("point"),
    //   buttons: [{ text: script.text("point_button"), target: "warmup:sound" }],
    // },
    // {
    //   id: "warmup:suspicious",
    //   type: "basic",
    //   html: script.html("wag"),
    //   buttons: [{ text: script.text("wag_button"), target: "warmup:sound" }],
    // },
    // {
    //   id: "warmup:sound",
    //   type: "basic",
    //   html: script.html("specific_sound"),
    //   buttons: [
    //     { text: script.text("specific_sound_button1") },
    //     { text: script.text("specific_sound_button2") },
    //   ],
    // },

    // PLAYDATE
    // --------

    {
      id: "playdate:beacon",
      type: "basic",
      header: {
        html: script.html("red_heart_emoji"),
        variant: "huge",
      },
      html: script.html("red_heart_text"),
      buttons: [{ text: script.text("red_heart_button") }],
      variant: "primary",
    },
    {
      id: "playdate:say-hi",
      type: "basic",
      html: script.html("polite"),
      buttons: [{ text: script.text("polite_button") }],
    },
    {
      id: "playdate:menu",
      type: "basic",
      html: script.html("buttons_1-15"),
      buttons: [
        // script.key("button_1.1"),
        script.key("button_2.1"),
        // script.key("button_3.1"),
        script.key("button_4.1"),
        // script.key("button_5.1"),
        // script.key("button_6.1"),
        // script.key("button_7.1"),
        // script.key("button_8.1"),
        // script.key("button_9.1"),
        // script.key("button_10.1"),
        script.key("button_11.1"),
        // script.key("button_12.1"),
        script.key("button_13.1"),
        script.key("button_14.1"),
        script.key("button_15.1"),
      ].map((target, index) => ({
        text: String(index + 1),
        target: `game:${target}`,
        canPickOnce: true,
      })),
    },

    // GAME SHARED
    // -----------

    {
      id: "game:outro",
      type: "basic",
      html: script.html("button_1.3_informative"),
      buttons: [{ text: script.text("button_1.3_button"), target: "playdate:beacon" }],
    },

    // GAME 1
    // ------
    //
    // {
    //   id: `game:${script.key("button_1.1")}`,
    //   type: "basic",
    //   html: script.html("button_1.1"),
    //   buttons: [
    //     { text: script.text("button_1.1_serious"), target: "game1:serious" },
    //     { text: script.text("button_1.1_silly"), target: "game1:silly" },
    //   ],
    // },
    // {
    //   id: "game1:serious",
    //   type: "basic",
    //   html: script.html("button_1.(a)2"),
    //   buttons: [{ text: script.text("button_1.(a)2_button"), target: "game:outro" }],
    // },
    // {
    //   id: "game1:silly",
    //   type: "basic",
    //   html: script.html("button_1.(b)2"),
    //   buttons: [{ text: script.text("button_1.(b)2_button"), target: "game:outro" }],
    // },

    // GAME 2
    // ------

    {
      id: `game:${script.key("button_2.1")}`,
      type: "basic",
      html: script.html("button_2.1"),
      buttons: [
        { text: script.text("button_2.1_serious"), target: "game2:serious" },
        { text: script.text("button_2.1_silly"), target: "game2:silly" },
      ],
    },
    {
      id: "game2:serious",
      type: "basic",
      html: script.html("button_2.(a)2"),
      buttons: [{ text: script.text("button_2.(a)2_button"), target: "game:outro" }],
    },
    {
      id: "game2:silly",
      type: "basic",
      html: script.html("button_2.(b)2"),
      buttons: [{ text: script.text("button_2.(b)2_button"), target: "game:outro" }],
    },

    // GAME 3
    // ------

    // {
    //   id: `game:${script.key("button_3.1")}`,
    //   type: "basic",
    //   html: script.html("button_3.1"),
    //   buttons: [
    //     { text: script.text("button_3.1_serious"), target: "game3:serious" },
    //     { text: script.text("button_3.1_silly"), target: "game3:silly" },
    //   ],
    // },
    // {
    //   id: "game3:serious",
    //   type: "basic",
    //   html: script.html("button_3.(a)2"),
    //   buttons: [{ text: script.text("button_3.(a)2_button"), target: "game:outro" }],
    // },
    // {
    //   id: "game3:silly",
    //   type: "basic",
    //   html: script.html("button_3.(b)2"),
    //   buttons: [{ text: script.text("button_3.(b)2_button"), target: "game:outro" }],
    // },

    // GAME 4
    // ------

    {
      id: `game:${script.key("button_4.1")}`,
      type: "basic",
      html: script.html("button_4.1"),
      buttons: [
        { text: script.text("button_4.1_serious"), target: "game4:serious" },
        { text: script.text("button_4.1_silly"), target: "game4:silly" },
      ],
    },
    {
      id: "game4:serious",
      type: "basic",
      html: script.html("button_4.(a)2"),
      buttons: [{ text: script.text("button_4.(a)2_button"), target: "game:outro" }],
    },
    {
      id: "game4:silly",
      type: "basic",
      html: script.html("button_4.(b)2"),
      buttons: [{ text: script.text("button_4.(b)2_button"), target: "game:outro" }],
    },

    // GAME 5
    // ------

    // {
    //   id: `game:${script.key("button_5.1")}`,
    //   type: "basic",
    //   html: script.html("button_5.1"),
    //   buttons: [
    //     { text: script.text("button_5.1_serious"), target: "game5:serious" },
    //     { text: script.text("button_5.1_silly"), target: "game5:silly" },
    //   ],
    // },
    // {
    //   id: "game5:serious",
    //   type: "basic",
    //   html: script.html("button_5.(a)2"),
    //   buttons: [{ text: script.text("button_5.(a)2_button"), target: "game:outro" }],
    // },
    // {
    //   id: "game5:silly",
    //   type: "basic",
    //   html: script.html("button_5.(b)2"),
    //   buttons: [{ text: script.text("button_5.(b)2_button"), target: "game:outro" }],
    // },

    // GAME 6
    // ------
    //
    // {
    //   id: `game:${script.key("button_6.1")}`,
    //   type: "countdown",
    //   html: script.html("button_6.1"),
    //   countdownLength: 20 * 1000,
    //   target: "game:outro",
    // },

    // GAME 7
    // ------

    // {
    //   id: `game:${script.key("button_7.1")}`,
    //   type: "basic",
    //   html: script.html("button_7.1"),
    //   buttons: [{ text: script.text("button_7.1_button") }],
    // },
    // {
    //   id: `game:${script.key("button_7.2")}`,
    //   type: "basic",
    //   html: script.html("button_7.2"),
    //   buttons: [{ text: script.text("button_7.2_button"), target: "game:outro" }],
    // },

    // GAME 8
    // ------

    // {
    //   id: `game:${script.key("button_8.1")}`,
    //   type: "basic",
    //   html: script.html("button_8.1"),
    //   buttons: [{ text: script.text("button_8.1_button") }],
    // },
    // {
    //   id: `game:${script.key("button_8.2")}`,
    //   type: "countdown",
    //   html: script.html("button_8.2"),
    //   target: `game:${script.key("button_8.3")}`,
    //   countdownLength: 14 * 1000,
    //   variant: "invisible",
    // },
    // {
    //   id: `game:${script.key("button_8.3")}`,
    //   type: "countdown",
    //   html: script.html("button_8.3"),
    //   target: "game:outro",
    //   countdownLength: 18 * 1000,
    //   variant: "invisible",
    // },

    // GAME 9
    // ------

    // {
    //   id: `game:${script.key("button_9.1")}`,
    //   type: "basic",
    //   html: script.html("button_9.1"),
    //   buttons: [{ text: script.text("button_9.1_button") }],
    // },
    // {
    //   id: `game:${script.key("button_9.2")}`,
    //   type: "countdown",
    //   html: script.html("button_9.2"),
    //   countdownLength: 14 * 1000,
    //   variant: "invisible",
    // },
    // {
    //   id: `game:${script.key("button_9.3")}`,
    //   type: "countdown",
    //   html: script.html("button_9.3"),
    //   countdownLength: 18 * 1000,
    //   variant: "invisible",
    // },
    // {
    //   id: `game:${script.key("button_9.4")}`,
    //   type: "basic",
    //   html: script.html("button_9.4"),
    //   buttons: [{ text: script.text("button_9.4_button"), target: "game:outro" }],
    // },

    // GAME 10
    // -------

    // {
    //   id: `game:${script.key("button_10.1")}`,
    //   type: "basic",
    //   html: script.html("button_10.1"),
    //   buttons: [{ text: script.text("button_10.1_button") }],
    // },
    // {
    //   id: `game:${script.key("button_10.2")}`,
    //   type: "countdown",
    //   html: script.html("button_10.2"),
    //   countdownLength: 14 * 1000,
    //   variant: "invisible",
    // },
    // {
    //   id: `game:${script.key("button_10.3")}`,
    //   type: "countdown",
    //   html: script.html("button_10.3"),
    //   countdownLength: 15 * 1000,
    //   variant: "invisible",
    // },
    // {
    //   id: `game:${script.key("button_10.4")}`,
    //   type: "basic",
    //   html: script.html("button_10.4"),
    //   buttons: [{ text: script.text("button_10.4_button"), target: "game:outro" }],
    // },

    // GAME 11
    // -------

    {
      id: `game:${script.key("button_11.1")}`,
      type: "basic",
      html: script.html("button_11.1"),
      buttons: [{ text: script.text("button_11.1_button"), target: "game:outro" }],
    },

    // GAME 12
    // -------
    //
    // {
    //   id: `game:${script.key("button_12.1")}`,
    //   type: "basic",
    //   html: script.html("button_12.1"),
    //   buttons: [{ text: script.text("button_12.1_button"), target: "game:outro" }],
    // },

    // GAME 13
    // -------

    {
      id: `game:${script.key("button_13.1")}`,
      type: "basic",
      html: script.html("button_13.1"),
      buttons: [{ text: script.text("button_13.1_button") }],
    },
    {
      id: `game:${script.key("button_13.2")}`,
      type: "basic",
      html: script.html("button_13.2"),
      buttons: [{ text: script.text("button_13.2_button"), target: "game:outro" }],
    },

    // GAME 14
    // -------

    {
      id: `game:${script.key("button_14.1")}`,
      type: "basic",
      html: script.html("button_14.1"),
      buttons: [{ text: script.text("button_14.1_button"), target: "game:outro" }],
    },

    // GAME 15
    // -------

    {
      id: `game:${script.key("button_15.1")}`,
      type: "basic",
      html: script.html("button_15.1"),
      buttons: [{ text: script.text("button_15.1_button"), target: "game:outro" }],
    },
  ];
}
